import { BaseMessage } from '@utilities/ChannelMessaging/BaseMessage.type';

/**
 * A generic channel messaging utility using a payload map.
 * @template PM - A mapping from message type keys (strings) to their payload shapes.
 */
export class ChannelMessaging<PM extends Record<string, unknown>> {
	private readonly port: MessagePort;

	constructor(port: MessagePort) {
		this.port = port;
	}

	private isValidMessage<K extends keyof PM & string>(
		event: MessageEvent,
		type: K,
	): event is MessageEvent<BaseMessage<K, PM[K]>> {
		const data = event.data as BaseMessage;
		return data.type === type;
	}

	public sendMessage<K extends keyof PM & string>(
		type: K,
		version: number,
		payload: PM[K],
	): void {
		const message: BaseMessage = { type, version, payload };
		this.port.postMessage(message);
	}

	public receiveMessage<K extends keyof PM & string>(
		type: K,
		callback: (payload: PM[K], version: number) => void,
	): () => void {
		this.port.onmessage = (event: MessageEvent): void => {
			if (!this.isValidMessage(event, type)) return;
			const { payload, version } = event.data;
			callback(payload, version);
		};

		return () => (this.port.onmessage = null);
	}
}
